import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { IPermitTypeField, ISiteLocation, LocationType } from '~/client/graph'
import {
  getLocationParentNames,
  LocationReference,
} from '~/client/src/shared/components/ObjectReference/LocationReference'
import { LINK_UNDERLINE_ON_GROUP_HOVER } from '~/client/src/shared/components/ObjectReference/ObjectReference'
import {
  getResourceChipLabel,
  IResourceChipItem,
} from '~/client/src/shared/components/ResourceChipList'
import { ResourceValueReference } from '~/client/src/shared/components/ResourceLink/ResourceValueReference'
import StruxhubAttributeSelector, {
  SelectedLocationTag,
} from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubAttributeSelector'
import StruxhubResourceSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubResourceSelector'
import { equipmentPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { ResourceSemanticIconKey } from '~/client/src/shared/resources/definitions'
import { LocationKind } from '~/client/src/shared/resources/domain/locationKind'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'
import { getResourcePlaceholderText } from '~/client/src/shared/utils/getResourcePlaceholderText'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'
import { copyObject } from '~/client/src/shared/utils/util'

import VerticalObjectLevelsSelector from '../../../VerticalObjectLevelsSelector/VerticalObjectLevelsSelector'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import HoursOfOperationNote, {
  renderResourceNote,
} from '../HoursOfOperationNote'
import { resolvePreciseLocationGlobeIds } from './components/preciseLocationHelpers'
import WorkflowFormPreciseLocation from './components/WorkflowFormPreciseLocation'
import PermitBaseFormField from './PermitBaseFormField'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number

  tagsStore?: TagsStore
  state?: InitialState
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
}

@inject('tagsStore', 'state', 'sitemapItemsStore', 'locationAttributesStore')
@observer
export default class PermitFormLocationField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      fieldsStore,
      store,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        viewModeRenderer={this.renderViewMode}
      />
    )
  }

  private renderComponent = (
    value: ISiteLocation,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
      store,
    } = this.localProps
    const { caption, isMandatory, isMultiple, allowPreciseLocation } = typeField
    const isEquipment = equipmentPermitFieldTypes.includes(typeField.type)

    // Equipment, and a location outside a table: one resource selector for all values,
    // each value still stored in its own slot.
    if (this.isChipsField) {
      if (index > 0) return null
      return isEquipment
        ? this.renderEquipmentChips()
        : this.renderLocationChips()
    }

    const { isFieldChanged } = fieldsStore
    const shouldShowPreciseLocation =
      allowPreciseLocation &&
      value?.id &&
      index === 0 &&
      this.hasPreciseLocationGlobe(value)

    return (
      <>
        <div className="row y-center">
          <StruxhubAttributeSelector
            className="overflow-hidden"
            label={caption}
            isRequired={isMandatory}
            isChanged={isFieldChanged(
              typeField.id,
              index,
              tableId,
              tableRowIndex,
              parentInstanceId,
              parentValueIndex,
            )}
            isLabelHidden={!!index && !isHeaderShown}
            isHelperTextCondensed={
              (isMultiple && !isHeaderShown) ||
              this.isVerticalObjectValue(value)
            }
            isTopMarginForced={!index}
            onClick={this.openLocationPicker.bind(this, index)}
            value={value?.id || ''}
            fallback={store.getLocationDisplayFallback(value?.id)}
            note={renderResourceNote(store, [value?.id])}
          />
        </div>
        {this.isVerticalObjectValue(value) && (
          <VerticalObjectLevelsSelector
            verticalObjectId={value.id}
            selectedLevelIds={value.levels || []}
            onChange={this.onLocationLevelsChange.bind(this, index)}
          />
        )}
        {shouldShowPreciseLocation && index === 0 && (
          <WorkflowFormPreciseLocation
            value={value}
            permitType={store.workflowFieldsStore?.template}
            store={store}
            saveLocation={newValue =>
              this.onPreciseLocationSave(index, newValue)
            }
            typeField={typeField}
          />
        )}
      </>
    )
  }

  private renderEquipmentChips(): JSX.Element {
    const { typeField, store } = this.localProps
    // Multi-select stores the array as a single field value — flatten
    const values = (this.rawFieldValues || []).flat() as ISiteLocation[]
    const items = values
      .filter(v => v?.id)
      .map(v => this.toEquipmentChipItem(v.id))
    const [primary] = items
    // A single saved equipment shows as its reference, like a single location; a deleted
    // or not-yet-saved one keeps its chip.
    const tagged =
      !typeField.isMultiple &&
      items.length === 1 &&
      !primary.isDeleted &&
      !store.pendingResources.has(primary.id)
        ? primary
        : null

    return this.renderChipsSelector({
      items,
      resourceTypeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
      semanticIcon: 'equipment',
      valueContent: tagged && <SelectedLocationTag value={tagged.id} />,
      locationIds: values.map(v => v?.id),
      onRemove: typeField.isMultiple ? this.removeEquipmentById : undefined,
    })
  }

  private renderLocationChips(): JSX.Element {
    const {
      typeField,
      fieldsStore,
      store,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const chips = this.fieldValues.flatMap((value, slot) =>
      value?.id
        ? [{ value, slot, item: this.toLocationChipItem(value.id) }]
        : [],
    )
    const [primary] = chips
    // A single location shows as its tag, with its LBS; a deleted one keeps its chip.
    const tagged =
      !typeField.isMultiple && chips.length === 1 && !primary.item.isDeleted
        ? primary.value
        : null

    return (
      <>
        {this.renderChipsSelector({
          items: chips.map(chip => chip.item),
          resourceTypeId: ResourceTypeId.LOCATIONS,
          semanticIcon: 'location',
          valueContent: tagged && (
            <SelectedLocationTag
              value={tagged.id}
              fallback={store.getLocationDisplayFallback(tagged.id)}
            />
          ),
          locationIds: chips.map(chip => chip.value.id),
          // Also when a field made single still holds the values it took while multiple.
          onRemove:
            typeField.isMultiple || chips.length > 1
              ? this.removeLocationById
              : undefined,
          isChanged: fieldsStore.isFieldChanged(
            typeField.id,
            undefined,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          ),
        })}
        {chips
          .filter(({ value }) => this.isVerticalObjectValue(value))
          .map(({ value, slot, item }) => (
            <VerticalObjectLevelsSelector
              key={value.id}
              label={tagged ? undefined : getResourceChipLabel(item)}
              verticalObjectId={value.id}
              selectedLevelIds={value.levels || []}
              onChange={levels => this.onLocationLevelsChange(slot, levels)}
            />
          ))}
        {primary &&
          typeField.allowPreciseLocation &&
          this.hasPreciseLocationGlobe(primary.value) && (
            <WorkflowFormPreciseLocation
              value={primary.value}
              permitType={store.workflowFieldsStore?.template}
              store={store}
              saveLocation={this.onPrimaryLocationSave}
              typeField={typeField}
            />
          )}
      </>
    )
  }

  private renderChipsSelector({
    items,
    resourceTypeId,
    semanticIcon,
    locationIds,
    onRemove,
    isChanged,
    valueContent,
  }: {
    items: IResourceChipItem[]
    resourceTypeId: ResourceTypeId
    semanticIcon: ResourceSemanticIconKey
    locationIds: string[]
    onRemove?: (id: string) => void
    isChanged?: boolean
    valueContent?: React.ReactNode
  }): JSX.Element {
    const { typeField, store } = this.localProps
    const {
      caption,
      isMandatory,
      allowSelectExisting = true,
      allowCreateNew = true,
    } = typeField
    const iconClassName =
      'tw:block tw:size-4 tw:text-muted-foreground tw:ml-0.5 tw:mr-1'

    return (
      <StruxhubResourceSelector
        className="overflow-hidden"
        label={caption}
        isRequired={isMandatory}
        isChanged={isChanged}
        isTopMarginForced
        items={items}
        value={items[0]?.id ?? ''}
        placeholderText={getResourcePlaceholderText(
          allowSelectExisting,
          allowCreateNew,
        )}
        allowSelectExisting={allowSelectExisting}
        onClick={() => this.openLocationPicker(0)}
        onSelectExisting={() => {
          store.setFieldSelectionMode('selectExisting')
          this.openLocationPicker(0)
        }}
        onRemove={onRemove}
        valueContent={valueContent}
        renderIcon={item =>
          renderResourceRecordIcon(
            resourceTypeId,
            item,
            renderResourceSemanticIcon(semanticIcon, iconClassName),
            iconClassName,
          )
        }
        note={renderResourceNote(store, locationIds)}
      />
    )
  }

  // Equipment deleted after a workflow was saved must still name itself, so the
  // lookup goes through the deleted-aware attribute map rather than TagsStore,
  // whose lists exclude deleted items by design.
  private toEquipmentChipItem = (id: string): IResourceChipItem => {
    const { store, locationAttributesStore } = this.localProps

    const pending = store.pendingResources.get(id)
    if (pending) {
      // Unsaved equipment is not in the store yet — read the glyph off the form data
      // so a pending chip matches its saved siblings.
      return {
        id,
        label: pending.name,
        color: pending.formData?.color as string,
        iconName: pending.formData?.iconName as string,
      }
    }

    const eq = locationAttributesStore.allItemsMapWithDeleted[
      id
    ] as LocationBase
    if (!eq) {
      // Unresolvable once equipment has loaded — gone beyond recovery. Until
      // then the id stands in rather than mislabelling a value still loading.
      return locationAttributesStore.offloadingEquipmentsStore.isDataReceived
        ? { id, isDeleted: true }
        : { id, label: id }
    }

    return {
      id,
      label: eq.stxResourceId ? `${eq.stxResourceId}: ${eq.name}` : eq.name,
      isDeleted: eq.isDeleted,
      // Resolved from the deleted-aware map, so even a deleted equipment keeps its glyph.
      color: eq.color,
      iconName: eq.iconName,
    }
  }

  private toLocationChipItem = (id: string): IResourceChipItem => {
    const { store, locationAttributesStore } = this.localProps

    const location = locationAttributesStore.allItemsMapWithDeleted[
      id
    ] as LocationBase
    if (location) {
      return {
        id,
        label: location.stxResourceId
          ? `${location.stxResourceId}: ${location.name}`
          : location.name,
        isDeleted: location.isDeleted,
        color: location.color,
        iconName: location.iconName,
      }
    }

    const fallback = store.getLocationDisplayFallback(id)
    if (fallback) {
      return {
        id,
        label: fallback.name,
        color: fallback.color,
        iconName: fallback.iconName,
      }
    }

    return locationAttributesStore.isDataReceived
      ? { id, isDeleted: true }
      : { id, label: id }
  }

  private openResourceCard = (
    id: string,
    resourceTypeId: string = ResourceTypeId.OFFLOADING_EQUIPMENT,
  ) => {
    const { state, store, locationAttributesStore } = this.localProps
    if (store.pendingResources.get(id)) return
    const resource = locationAttributesStore.getById(id) as LocationBase
    if (!resource || resource.isDeleted) return
    state.setResourceDisplayedOnGlobalCard(id, resourceTypeId)
  }

  private renderViewMode = (value: ISiteLocation): JSX.Element => {
    const { tagsStore, typeField, locationAttributesStore, store } =
      this.localProps
    const isEquipment = equipmentPermitFieldTypes.includes(typeField.type)

    const location = (
      isEquipment
        ? locationAttributesStore.allItemsMapWithDeleted[value?.id]
        : locationAttributesStore.getLocationBySiteLocationObj(value)
    ) as LocationBase
    const label =
      isEquipment && value?.id
        ? getResourceChipLabel(this.toEquipmentChipItem(value.id))
        : (location?.name ?? store.getLocationDisplayFallback(value?.id)?.name)

    if (!location?.id) {
      return (
        <div className="text large word-break-all">{label || NO_VALUE}</div>
      )
    }

    const shouldShowPreciseLocation =
      this.localProps.typeField.allowPreciseLocation &&
      this.fieldValues[0]?.id === value?.id &&
      this.hasPreciseLocationGlobe(value) &&
      this.hasPinLocation(value)

    const resourceTypeId = isEquipment
      ? ResourceTypeId.OFFLOADING_EQUIPMENT
      : ResourceTypeId.LOCATIONS
    const isOpenable =
      !location.isDeleted && !store.pendingResources?.get(value.id)
    const name = location.isDeleted
      ? getDeletedObjectLabel(location.name)
      : location.name

    const reference = (
      <LocationReference
        iconName={location.iconName}
        color={location.color}
        variant="plain"
        size="dense"
        parentNames={getLocationParentNames(
          location,
          tagsStore.tagStoreByTagTypeMap,
        )}
        resourceCode={location.stxResourceId}
      >
        <span title={name} className={LINK_UNDERLINE_ON_GROUP_HOVER}>
          {name}
        </span>
      </LocationReference>
    )

    return (
      <div className="overflow-hidden">
        <ResourceValueReference
          resourceTypeId={resourceTypeId}
          recordId={location.id}
          projectId={this.localProps.state.activeProject?.id}
          onOpen={
            isOpenable
              ? () => this.openResourceCard(location.id, resourceTypeId)
              : undefined
          }
        >
          {reference}
        </ResourceValueReference>
        <HoursOfOperationNote
          store={this.localProps.store}
          locationIds={[value.id]}
          isViewMode
          className="tw:mb-3 tw:ml-6"
        />
        {shouldShowPreciseLocation && (
          <WorkflowFormPreciseLocation
            value={value}
            permitType={this.localProps.store.workflowFieldsStore?.template}
            store={this.localProps.store}
            isViewOnly={true}
            typeField={this.localProps.typeField}
          />
        )}
      </div>
    )
  }

  // The levels picker belongs to vertical objects. Decided on the live location, not the
  // value's stored type, which is a snapshot a re-kind leaves behind (STX1-597).
  private isVerticalObjectValue = (value: ISiteLocation): boolean =>
    this.localProps.locationAttributesStore.isSiteLocationOfKind(
      value,
      LocationKind.VerticalObject,
    )

  private get fieldValues(): ISiteLocation[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    const values = fieldsStore.getFieldValues<ISiteLocation>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )
    return values?.length ? values : [{ id: null }]
  }

  private openLocationPicker(fieldIndex: number) {
    const {
      store,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    store.setSelectedField(
      typeField,
      fieldIndex,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )

    if (equipmentPermitFieldTypes.includes(typeField.type)) {
      store.setSelectedLocationType(LocationType.OffloadingEquipment)
    }

    if (typeField.isMultiple) {
      store.setSelectedOnChangeHandler(
        this.onChangeMultipleLocations.bind(this, fieldIndex),
      )
    }
  }

  private onChangeMultipleLocations(
    fieldIndex: number,
    locations: ISiteLocation[] | ISiteLocation,
  ) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    if (typeField.isMultiple && Array.isArray(locations)) {
      fieldsStore.changeLocationFieldValues(
        typeField,
        locations,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    const singleValue = Array.isArray(locations)
      ? locations[fieldIndex] || null
      : locations || null

    if (!tableId) {
      fieldsStore.changeFieldValue(
        typeField,
        fieldIndex,
        singleValue,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    fieldsStore.changeTableFieldValue(
      tableId,
      tableRowIndex,
      typeField,
      fieldIndex,
      singleValue,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.removeFieldValue(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
      // Removal bypasses the picker path; re-run the Save-gate validation.
      this.props.store.scheduleLocationsValidation()
    }
  }

  private removeEquipmentById = (id: string) => {
    const { store } = this.props
    // Re-validate so the Save gate updates after removing the value.
    store.scheduleLocationsValidation()

    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    const rawValues = fieldsStore.getFieldValues<
      ISiteLocation | ISiteLocation[]
    >(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    // Multi-select stores the array as a single field value
    const firstValue = rawValues?.[0]
    if (Array.isArray(firstValue)) {
      // Stored as single array — filter out the removed ID and save back
      const updated = firstValue.filter(v => v?.id !== id)
      const newValue = updated.length > 0 ? updated : null

      if (this.isTableField) {
        fieldsStore.changeTableFieldValue(
          tableId,
          tableRowIndex,
          typeField,
          0,
          newValue,
          parentInstanceId,
          parentValueIndex,
        )
      } else {
        fieldsStore.changeFieldValue(
          typeField,
          0,
          newValue,
          parentInstanceId,
          parentValueIndex,
        )
      }
    } else {
      // Stored as individual field values
      const index = (rawValues || []).findIndex(
        v => !Array.isArray(v) && v?.id === id,
      )
      if (index >= 0) {
        this.removeValue(index)
      }
    }
    // After the write, so new equipment another field still uses is kept.
    store.releasePendingResourceIfUnreferenced(id)
  }

  private removeLocationById = (id: string) => {
    const {
      store,
      fieldsStore,
      typeField,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    fieldsStore.changeLocationFieldValues(
      typeField,
      this.fieldValues.filter(v => v?.id && v.id !== id),
      parentInstanceId,
      parentValueIndex,
    )
    // After the write, so a new location another field still uses is kept.
    store.releasePendingResourceIfUnreferenced(id)
    store.scheduleLocationsValidation()
  }

  private onPrimaryLocationSave = (newValue: ISiteLocation) => {
    const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
      this.localProps

    fieldsStore.changePrimaryLocationValue(
      typeField,
      newValue,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private onLocationLevelsChange(index: number, levels: string[]) {
    const location = copyObject<ISiteLocation>(this.fieldValues[index])
    location.levels = [...levels]

    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeFieldValue, changeTableFieldValue } = fieldsStore

    if (this.isTableField) {
      changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        index,
        location,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    changeFieldValue(
      typeField,
      index,
      location,
      parentInstanceId,
      parentValueIndex,
    )
  }

  // A table cell's: every other field saves its pin through onPrimaryLocationSave.
  private onPreciseLocationSave(index: number, newValue: ISiteLocation) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    fieldsStore.changeTableFieldValue(
      tableId,
      tableRowIndex,
      typeField,
      index,
      newValue,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private hasPreciseLocationGlobe(value: ISiteLocation): boolean {
    const { state, store } = this.localProps
    return resolvePreciseLocationGlobeIds({ state, store, value }).length > 0
  }

  private hasPinLocation(value: ISiteLocation): boolean {
    if (
      value?.preciseLocation?.latitude != null &&
      value?.preciseLocation?.longitude != null
    ) {
      return true
    }
    if (value?.id) {
      const sitemapItem = this.localProps.sitemapItemsStore?.list.find(
        item => item.assignedId === value.id,
      )
      return !!sitemapItem?.coordinates
    }
    return false
  }

  private get isChipsField(): boolean {
    return (
      equipmentPermitFieldTypes.includes(this.localProps.typeField.type) ||
      !this.isTableField
    )
  }

  private get rawFieldValues() {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    return fieldsStore.getFieldValues<ISiteLocation | ISiteLocation[]>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )
  }

  // localProps, not props: isChipsField reads this while the parent field renders.
  private get isTableField(): boolean {
    return !!this.localProps.tableId
  }
}
